import { useState } from 'react'
import { useAccountSettings, useUpdateAccountSettings } from '@/hooks/useAccountSettings'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { SettingsSaveError } from '@/lib/settingsSaveError'
import type { CustomerSource } from '@/types'

export const DEFAULT_CUSTOMER_SOURCES: CustomerSource[] = [
  { key: 'phone', en: 'Phone', th: 'โทรศัพท์' },
  { key: 'facebook', en: 'Facebook', th: 'Facebook' },
  { key: 'line', en: 'LINE', th: 'LINE' },
  { key: 'walk_in', en: 'Walk-in', th: 'เข้ามาเอง' },
  { key: 'referral', en: 'Referral', th: 'แนะนำ' },
  { key: 'external', en: 'External', th: 'ภายนอก' },
]

export function useSourceSettings() {
  const { data: account } = useAccountSettings()
  const updateSettings = useUpdateAccountSettings()
  const translateError = useApiErrorTranslator('settings')
  // PAY-1 (T149-084): no client validation here (none invented), so this is
  // only ever set on a server failure. Cleared on the next save attempt.
  const [saveError, setSaveError] = useState<SettingsSaveError | null>(null)

  // null means "not yet edited by user" — use server data
  const [localSources, setLocalSources] = useState<CustomerSource[] | null>(null)
  const [newSourceEn, setNewSourceEn] = useState('')
  const [newSourceTh, setNewSourceTh] = useState('')

  // Derive current sources: prefer local edits, fall back to server data
  const serverSources =
    account?.settings.customer_sources?.length
      ? account.settings.customer_sources
      : DEFAULT_CUSTOMER_SOURCES
  const sources = localSources ?? serverSources

  const setSources = (updater: CustomerSource[] | ((prev: CustomerSource[]) => CustomerSource[])) => {
    setLocalSources((prev) => {
      const current = prev ?? serverSources
      return typeof updater === 'function' ? updater(current) : updater
    })
  }

  const addSource = () => {
    const enTrimmed = newSourceEn.trim()
    const thTrimmed = newSourceTh.trim()
    if (!enTrimmed) return
    const key = enTrimmed.toLowerCase().replace(/\s+/g, '_')
    if (sources.some((s) => s.key === key)) return
    setSources((prev) => [...prev, { key, en: enTrimmed, th: thTrimmed || enTrimmed }])
    setNewSourceEn('')
    setNewSourceTh('')
  }

  const removeSource = (index: number) => {
    setSources((prev) => prev.filter((_, i) => i !== index))
  }

  const resetSources = () => {
    setLocalSources(DEFAULT_CUSTOMER_SOURCES)
  }

  // PAY-1 (T149-084): returns true only when the save actually happened.
  // The caller shows success only on true.
  const handleSave = async (): Promise<boolean> => {
    setSaveError(null)
    try {
      await updateSettings.mutateAsync({
        settings: { customer_sources: sources },
      })
      return true
    } catch (err) {
      setSaveError({ serverMessage: translateError(err) })
      return false
    }
  }

  const isDirty = JSON.stringify(sources) !== JSON.stringify(account?.settings.customer_sources ?? DEFAULT_CUSTOMER_SOURCES)

  return {
    sources,
    newSourceEn,
    newSourceTh,
    setNewSourceEn,
    setNewSourceTh,
    isPending: updateSettings.isPending,
    isSaving: updateSettings.isPending,
    isDirty,
    saveError,
    addSource,
    removeSource,
    resetSources,
    handleSave,
  }
}
